import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t, getal, wanneer } from '../i18n.js';
import Grafiek from '../components/Grafiek.jsx';
import Lijngrafiek from '../components/Lijngrafiek.jsx';
import { St } from '../components/Status.jsx';
import Balken from '../components/Balken.jsx';

/**
 * Grafieken uit Cisco Umbrella (docs/besluiten/umbrella-grafieken.md): de securitygebeurtenissen die ZERO elke vijf
 * minuten ophaalt, per uur of dag, met de categorieën, apparaten en domeinen. Voor medewerkers (de gekozen klant), voor
 * klanten (hun eigen) en bij meekijken: `haal(dagen)` bepaalt waar het vandaan komt.
 *
 * Twee delen: **alle DNS-aanvragen** (per uur uit Umbrella bewaard, de top live) en **security** (wat ZERO indeelde).
 *
 * Kleuren uit het systeem. Alle DNS: een lijngrafiek van de aanvragen in ADTS-cyaan, en de geblokkeerde aanvragen
 * als tabel (tientallen tegen honderdduizenden: in een grafiek met één as zie je ze niet). Security: geblokkeerd in cyaan, doorgelaten in de statuskleur kritiek (een stand, met een woord in de legenda en
 * de tooltip). Eén reeks: alleen cyaan.
 */

export default function Umbrella({ haal, sleutel, sub }) {
  const [dagen, zetDagen] = useState(7);
  const PERIODES = [[1, t('24 uur')], [7, t('7 dagen')], [30, t('30 dagen')]];
  const q = useQuery({ queryKey: [...sleutel, 'umbrella', dagen], queryFn: () => haal(dagen), refetchInterval: 60_000 });
  const kop = (
    <div className="page-head"><div>
      <h1>DNS Security</h1>
      <p className="sub">{sub ? `${sub} · ` : ''}{t('Wat Cisco Umbrella tegenhield of doorliet in de securitycategorieën')}</p>
    </div></div>
  );
  const filters = (
    <div className="filters">
      {PERIODES.map(([d, l]) => <button key={d} type="button" className="chip" aria-pressed={dagen === d} onClick={() => zetDagen(d)}>{l}</button>)}
    </div>
  );
  if (q.isPending) return <>{kop}{filters}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}{filters}<div className="melder rood">{t(q.error.message)}</div></>;
  const g = q.data.data;
  const tot = g.totals;
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{typeof n === 'number' ? getal(n) : n}</div><div className="t">{label}</div></div>;
  const vakken = g.tijd.map((v) => ({ from: v.van, gericht: v.doorgelaten, scan: v.geblokkeerd }));

  const dns = g.dns;
  /* Een klein aandeel met genoeg cijfers om niet als 0 te tonen (0,04%). */
  const aandeel = dns.totals.requests ? (dns.totals.blocked / dns.totals.requests) * 100 : 0;
  const procent = aandeel && aandeel < 1 ? aandeel.toLocaleString('nl-NL', { maximumSignificantDigits: 2 }) : getal(Math.round(aandeel * 10) / 10);

  return (
    <>
      {kop}{filters}
      <h2 className="sectiekop">{t('Alle DNS-aanvragen')}</h2>
      <div className="tiles">
        {tegel(dns.totals.requests, t('DNS-aanvragen'))}
        {tegel(dns.totals.blocked, t('geblokkeerd ({p}%)', { p: procent }))}
        {tegel(dns.top?.domeinenTotaal ?? '—', t('verschillende domeinen'))}
        {tegel(dns.top?.apparatenTotaal ?? '—', t('apparaten en netwerken'))}
      </div>
      {!dns.totals.requests ? (
        <section className="card" style={{ marginBottom: 18 }}><p className="empty">{t('Nog geen DNS-aanvragen opgehaald. ADTS ZERO haalt ze elke vijf minuten op bij een gekoppelde klant; de eerste keer de laatste 30 dagen.')}</p></section>
      ) : (
        <section className="card" style={{ marginBottom: 18 }}>
          <header><h2>{dns.per === 'hour' ? t('Aanvragen per uur') : t('Aanvragen per dag')}</h2></header>
          <Lijngrafiek punten={dns.tijd.map((v) => ({ van: v.van, n: v.toegestaan + v.geblokkeerd }))} perDag={dns.per !== 'hour'}
            label={t('DNS-aanvragen per tijdvak')} />
        </section>
      )}
      {dns.top && (
        <div className="grid-2" style={{ marginBottom: 18 }}>
          <section className="card"><header><h2>{t('Meest gevraagde domeinen')}</h2></header>
            <Balken rijen={dns.top.domeinen.map((d) => ({ naam: d.naam, n: d.n, extra: d.geblokkeerd ? t('{n} geblokkeerd', { n: getal(d.geblokkeerd) }) : null }))} /></section>
          <section className="card"><header><h2>{t('Categorieën van alle aanvragen')}</h2></header>
            <Balken rijen={dns.top.categorieen.map((c) => ({ naam: c.naam, n: c.n }))} /></section>
        </div>
      )}
      {dns.top && (
        <section className="card" style={{ marginBottom: 18 }}>
          <header><h2>{t('Geblokkeerde aanvragen')}</h2><span className="meta">{t('{n} in deze periode', { n: getal(dns.totals.blocked) })}</span></header>
          {dns.top.geblokkeerd?.length ? (
            <table>
              <thead><tr><th>{t('Domein')}</th><th>{t('Categorie')}</th><th style={{ textAlign: 'right' }}>{t('Keer')}</th><th>{t('Apparaten')}</th><th>{t('Laatst')}</th></tr></thead>
              <tbody>{dns.top.geblokkeerd.map((d) => (
                <tr key={d.naam}>
                  <td className="mono" style={{ overflowWrap: 'anywhere' }}>{d.naam}</td>
                  <td>{d.categorieen.join(', ') || '—'}</td>
                  <td className="num" style={{ textAlign: 'right' }}>{getal(d.n)}</td>
                  <td style={{ overflowWrap: 'anywhere' }}>{d.apparaten.slice(0, 3).join(', ') || '—'}{d.apparaten.length > 3 && <span className="muted"> +{d.apparaten.length - 3}</span>}</td>
                  <td className="muted">{d.laatst ? wanneer(d.laatst) : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p className="empty">{t('Niets geblokkeerd in deze periode.')}</p>}
        </section>
      )}
      {dns.error && <div className="callout warn" style={{ marginBottom: 18 }}>{t('De top uit Umbrella kwam niet: {fout}', { fout: dns.error })}</div>}

      <h2 className="sectiekop">{t('Security')}</h2>
      <div className="tiles">
        {tegel(tot.n, t('securitygebeurtenissen'))}
        {tegel(tot.doorgelaten, t('doorgelaten naar een dreiging'), tot.doorgelaten ? 'crit' : '')}
        {tegel(tot.besmet, t('malware of C2 geblokkeerd'), tot.besmet ? 'warn' : '')}
        {tegel(tot.apparaten, t('apparaten'))}
      </div>
      {!tot.n ? (
        <section className="card"><p className="empty">{t('Geen securitygebeurtenissen in deze periode. Umbrella hield niets tegen en liet niets door in de securitycategorieën.')}</p></section>
      ) : (
        <>
          <section className="card" style={{ marginBottom: 18 }}>
            <header><h2>{g.per === 'hour' ? t('Per uur') : t('Per dag')}</h2>
              <span className="meta legenda">
                <span><i style={{ background: 'var(--cyan-ink)' }} />{t('geblokkeerd')}</span>
                <span><i style={{ background: 'var(--st-critical)' }} />{t('doorgelaten')}</span>
              </span></header>
            <Grafiek vakken={vakken} stap={g.per === 'hour' ? 3600 : 86400} reeksen={{
              label: t('Securitygebeurtenissen per tijdvak'), kleurBoven: 'var(--st-critical)',
              totaal: (n) => t('{n} gebeurtenissen', { n }), boven: (n) => t('doorgelaten: {n}', { n }), onder: (n) => t('geblokkeerd: {n}', { n }),
            }} />
          </section>
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <section className="card"><header><h2>{t('Categorieën')}</h2></header><Balken rijen={g.categorieen.map((c) => ({ naam: c.naam, n: c.n }))} /></section>
            <section className="card"><header><h2>{t('Apparaten')}</h2></header>
              <Balken rijen={g.apparaten.map((a) => ({ naam: a.naam, n: a.n, extra: a.doorgelaten ? t('{n} doorgelaten', { n: getal(a.doorgelaten) }) : null }))} /></section>
          </div>
          <section className="card">
            <header><h2>{t('Domeinen')}</h2></header>
            <table>
              <thead><tr><th>{t('Domein')}</th><th>{t('Categorie')}</th><th style={{ textAlign: 'right' }}>{t('Keer')}</th>
                <th style={{ textAlign: 'right' }}>{t('Apparaten')}</th><th>{t('Laatst')}</th></tr></thead>
              <tbody>{g.domeinen.map((d) => (
                <tr key={d.naam}>
                  <td className="mono" style={{ overflowWrap: 'anywhere' }}>{d.naam}
                    {d.doorgelaten > 0 && <> <St soort="critical">{t('{n} doorgelaten', { n: getal(d.doorgelaten) })}</St></>}</td>
                  <td>{d.categorie ?? '—'}</td><td className="num" style={{ textAlign: 'right' }}>{getal(d.n)}</td>
                  <td className="num" style={{ textAlign: 'right' }}>{getal(d.apparaten)}</td><td className="muted">{wanneer(d.laatst)}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
        </>
      )}
    </>
  );
}
